import dayjs from 'dayjs';
import {
    getFuturePotentialCategory,
    getPositionMatch,
    projectLevelByAge,
    PROMOTE_NOW,
    PROMOTE_SOON,
    KEEP_ROLE,
} from './employees';
import { DEFAULT_POTENTIAL_RULESET } from './potentialDSL';
import { getDynamicLabel } from './dynamicFields';
import { getText } from '../components/common/MultiLanguageText';
import { getJacquesPotential } from './potential';
import { idColumnLabel } from './downloadCsv';
import { performanceText } from '../components/common/Texts';
import { aiLevelDownloadText } from './aiLevel';
import { alternativeLabelsText } from './alternativeLabels';

// Shared row-builder for every CSV/XLSX download (Dashboard's Capacity Map,
// Results, ...), so all pages export the same columns computed the same way
// from the raw /reply/ payload, regardless of what each page has already
// derived for its own UI. `slideAge` only matters for Dashboard, which lets
// users project level/potential forward in time; Results always downloads
// the current (non-projected) level by leaving it at 0.
//
// Replies without a valid computed level (invalid, or still in progress) are
// left out entirely: neither page's download should list someone whose
// current level isn't known yet.
//
// `includePerformance` adds the performance column with each reply's current
// value (empty when it has none), so the exported file can be edited and
// re-uploaded through "Carga desempeño" (see ReplyPerformanceBulk in the
// backend): rows left empty are ignored there, so a round-trip only changes
// what the user actually filled in. The exported text has to stay readable by
// PERFORMANCE_TEXT_TO_VALUE in talentapp/serializers/reply.py.
//
// `includeAiLevel` adds the AI level column (the last run of the AI service).
// It is staff-only information: the backend only sends `ai_classification` to
// staff users, and only the Results screen — which shows the same column —
// asks for it.
//
// `includeAlternatives` adds the alternative classifications the staff marked
// as acceptable (e.g. "3C, 4A"). Same story: staff-only, and only Results.
export function buildReplyDownloadRows(replies, {
    company, language, tr, slideAge = 0, includePerformance = false, includeAiLevel = false,
    includeAlternatives = false,
}) {
    const ruleset = company?.potential_ruleset || DEFAULT_POTENTIAL_RULESET;
    const dynamicFieldDefs = Array.isArray(company?.dynamic_fields) ? company.dynamic_fields : [];
    const getName = (obj) => getText(obj, 'name', language);
    const MATCH_LABELS = {
        [PROMOTE_NOW]: tr('cap_higher_role'),
        [PROMOTE_SOON]: tr('cap_equal_role'),
        [KEEP_ROLE]: tr('cap_less_role'),
    };

    const rows = [];
    for (const result of (replies || [])) {
        if (result.invalid) continue;
        const pot = getJacquesPotential(result, language);
        if (!pot) continue;

        const position = company?.positions?.find((x) => x.id === result.position);
        const potentialAge = result.age + slideAge;
        const { level, sublevel } = projectLevelByAge(
            pot.level, pot.sublevel, slideAge, potentialAge,
            pot.progresionJacques, pot.edadRangoActual,
        );
        const altoPot = getFuturePotentialCategory(pot.nivelPotencial, pot.tuCurva, ruleset) === 'HIGH';
        const matchLabel = position ? MATCH_LABELS[getPositionMatch(position, level, sublevel)] : '';

        const row = {
            [idColumnLabel(company, tr)]: result.user_generic_id,
            [tr('name')]: result.name,
            [tr('age')]: result.age,
            [tr('current_level')]: `${level}${sublevel || ''}`,
            [tr('potential')]: pot.nivelPotencial,
            [tr('business_unit')]: getName(company?.business_units?.find((x) => x.id === result.business_unit)),
            [tr('department')]: getName(company?.departments?.find((x) => x.id === result.department)),
            [tr('position')]: getName(position),
            [tr('internal_group')]: getName(company?.grupos_internos?.find((x) => x.id === result.grupo_interno)),
            [tr('match_person_role')]: matchLabel,
            [tr('high_potential')]: altoPot ? tr('yes') : '',
            [tr('completion_date')]: result.completed_at ? dayjs(result.completed_at).format('YYYY-MM-DD') : '',
        };
        if (includeAiLevel) {
            row[tr('level_ai')] = aiLevelDownloadText(result.ai_classification);
        }
        if (includeAlternatives) {
            row[tr('alternative_labels_short')] = alternativeLabelsText(result.alternative_labels);
        }
        if (includePerformance) {
            row[tr('performance')] = result.performance ? performanceText(result.performance, tr) : '';
        }
        for (const f of dynamicFieldDefs) {
            const v = (result.dynamic_answers || {})[f.key];
            row[getDynamicLabel(f, language)] = (v === undefined || v === null) ? '' : v;
        }
        rows.push(row);
    }
    return rows;
}
